import { useState } from 'react'
import { RootState } from 'store'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store/hooks'
import { actions as dashboardActions } from 'store/dashboard'
import { Applicant, MultisectorData, ThirdPartyData } from 'store/dashboard/types'
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import AutorenewIcon from '@mui/icons-material/Autorenew'
import DownloadIcon from '@mui/icons-material/Download'
import Spinner from 'components/spinner/Spinner'
import OpenCloseButton from 'components/openCloseButton/OpenCloseButton'
import SectionContainer from '../../../components/SectionContainer'
import SectionHeader from '../../../components/SectionHeader'
import SectionSubTitle from '../../../components/SectionSubtitle'
import SectorsFilterComponent from './SectorsFilterComponent'
import { toTitleCase } from 'containers/user/utils'
import RoundIconButton from 'components/roundIconButton/RoundIconButton'
import { useAdminQuery } from 'containers/dashboard/multisectorReport/hooks/useAdminQuery'

type Props = {
  id: string
  data: MultisectorData
  thirdPartyDataList: ThirdPartyData[] | null
  subgroups: string[]
  onSubgroupsSelectedChange: (selectedSubgroups: string[]) => void
  regenerateMultisector: () => void
  target: Applicant
}

export default function InformationSection({
  id,
  data,
  thirdPartyDataList,
  subgroups,
  onSubgroupsSelectedChange,
  regenerateMultisector,
  target,
}: Props) {
  const dispatch = useAppDispatch()
  const zipIsLoading = useSelector((state: RootState) => state.dashboard.loading.multisectorZip) //TODO
  const roles = useSelector((state: RootState) => state.account.roles)
  const [sectionOpened, setSectionOpened] = useState(false)
  const [showSearches, setShowSearches] = useState(false)
  const [showThirdPartyJson, setShowThirdPartyJson] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { isAdminView, toggleAdmin } = useAdminQuery()
  const isAdminRole = roles?.includes('bo_admin') || roles?.includes('admin')
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const exportZip = () => {
    dispatch(dashboardActions.exportMultisectorZip(id))
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <Stack direction="row" alignItems="center">
          <Typography variant="h6">
            Peer Group Analysis for{' '}
            <Typography variant="h6" component="span" color="primary">
              {toTitleCase(data.info.company_names_selected?.[0]) || ''}
            </Typography>
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center">
          {isAdminRole && (
            <FormControlLabel
              control={<Switch checked={isAdminView} onChange={e => toggleAdmin(e.target.checked)} size="small" />}
              label="Admin view"
              sx={{ mr: 4, '.MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'gray' } }}
            />
          )}
          <Button
            id="action-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            sx={{ mr: 4 }}>
            Actions
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'action-button',
            }}>
            <MenuItem onClick={regenerateMultisector}>
              <RoundIconButton onClick={() => setSectionOpened(!sectionOpened)}>
                <AutorenewIcon sx={{ width: 17 }} />
              </RoundIconButton>
              Regenerate report
            </MenuItem>
            {data && (
              <Box>
                <MenuItem onClick={exportZip}>
                  <RoundIconButton>
                    <DownloadIcon sx={{ cursor: 'pointer', width: 17 }} />
                  </RoundIconButton>
                  Export zip{zipIsLoading && <Spinner />}
                </MenuItem>
                {isAdminRole && (
                  <MenuItem onClick={() => setShowSearches(!showSearches)}>
                    {showSearches ? <>Hide searches</> : <>Show selected searches </>}
                  </MenuItem>
                )}
              </Box>
            )}
            {isAdminRole && (
              <MenuItem onClick={() => setShowThirdPartyJson(!showThirdPartyJson)}>
                {showThirdPartyJson ? <>Hide third party data</> : <>Show third party json</>}
              </MenuItem>
            )}
          </Menu>
          <OpenCloseButton onClick={() => setSectionOpened(!sectionOpened)} opened={sectionOpened} />
        </Stack>
      </SectionHeader>
      <Collapse in={sectionOpened}>
        <Typography sx={{ ml: 3, mb: 1 }} variant="body2" color={'text.secondary'} mr={4}>
          {data.info.created_at}
        </Typography>
        <SectorsFilterComponent
          data={data}
          subgroups={subgroups}
          onSubgroupsSelectedChange={onSubgroupsSelectedChange}
          target={target}
        />
        <Paper variant="outlined" sx={{ borderRadius: '20px', m: 3, mt: 0 }}>
          <SectionSubTitle title="Search dimensions" />

          <Divider />
          {data && (
            <>
              <TableContainer sx={{ maxHeight: 700 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell key={1}>Sectors</TableCell>
                      <TableCell key={2}>Search limit</TableCell>
                      <TableCell key={3}>Start date </TableCell>
                      <TableCell key={4}>End date</TableCell>
                      <TableCell key={5}>Patent searches</TableCell>
                      <TableCell key={6}>Report length</TableCell>
                      <TableCell key={7}>Only active patents</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell key={1}>{data.info.subgroups.join(', ')}</TableCell>
                      <TableCell key={2}>{data.info.search_limit}</TableCell>
                      <TableCell key={3}>{data.info.start_date}</TableCell>
                      <TableCell key={4}>{data.info.end_date}</TableCell>
                      <TableCell key={5}>{data.info.searches_selected_length}</TableCell>
                      <TableCell key={6}>{data.multisector.length}</TableCell>
                      <TableCell key={7}>{String(Boolean(data.info.only_protected_patents))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {showSearches && (
                <Box m={2}>
                  <Typography color={'primary.main'} fontSize={16}>
                    Searches selected:
                  </Typography>

                  <List>
                    {data.info.searches_selected.map(search => (
                      <ListItem disablePadding> - {search.name}</ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {showThirdPartyJson && thirdPartyDataList && (
                <Box m={2}>
                  <Typography color={'primary.main'} fontSize={16}>
                    Third party data:
                  </Typography>
                  <List>
                    {thirdPartyDataList.map(tp => (
                      <ListItem disablePadding sx={{ py: 1 }}>
                        {JSON.stringify(tp)}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
          <Stack direction="row" justifyContent="space-between"></Stack>
        </Paper>
      </Collapse>
    </SectionContainer>
  )
}
